import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../../services/api';
import { toast } from 'react-toastify';
import { flattenQuery } from 'helpers/flattenQuery';
import { extractErrorMessage } from 'helpers/extractErrorMessage';
import { getZones as fetchZones, type ZoneRow, type ZonesPage } from 'api/zones/endpoints/getZones';
import { deleteZone as deleteZoneRequest } from 'api/zones/endpoints/deleteZone';
import type { AuthorizedZone } from 'api/zones/endpoints/getAuthorizedZones';
import type { FilterConfig, ListColumnConfig } from 'api/shared/listConfig';

// The shared dropdown/list cache of zones. Both /zones reads (getZones,
// getZonesPaginated) contribute the validated ZoneRow; getAuthorizedZones still
// hits the /lists/zones LOV directly and contributes the narrower, unvalidated
// AuthorizedZone — the one remaining loose writer (a future /lists LOV migration
// closes that gap). Typed as the union the slice actually holds; consumers read
// only the common fields (zone_id / name / description).
type ZoneListRow = ZoneRow | AuthorizedZone;

interface PaginationState {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

interface ZonesState {
  zones: ZoneListRow[];
  pagination: PaginationState;
  status: string | null;
  error: string | null;
  config: ListColumnConfig[];
  filtersConfig: FilterConfig[];
  loading: {
    getZones: boolean;
    getAuthorizedZones: boolean;
    getZonesPaginated: boolean;
    deleteZone: boolean;
  };
}

const initialState: ZonesState = {
  zones: [],
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 2000,
  },
  status: null,
  error: null,
  config: [],
  filtersConfig: [],
  loading: {
    getZones: false,
    getAuthorizedZones: false,
    getZonesPaginated: false,
    deleteZone: false,
  },
};

// GET /zones (non-paginated) — the dropdown/list cache; consumers read state.zones.
export const getZones = createAsyncThunk<ZoneRow[], void, { rejectValue: string }>('zones/getZones', async (_, { rejectWithValue }) => {
  try {
    const page = await fetchZones({ limit: initialState.pagination.pageSize });
    return [...page.zones];
  } catch (err) {
    return rejectWithValue(extractErrorMessage(err, 'Failed to fetch zones'));
  }
});

// GET /lists/zones — permission-filtered LOV. Still a direct api.* call: it's a
// separate route feeding many pages, migrated in a future /lists LOV pass, not
// this zones vertical. Left untyped/unvalidated (the documented multi-writer gap).
export const getAuthorizedZones = createAsyncThunk('zones/getAuthorizedZones', async (args?: { resourceType?: string; action?: string }) => {
  const params: Record<string, string> = { authorized: 'true' };
  if (args?.resourceType) {
    params.resource_type = args.resourceType;
    if (args.action) {
      params.action = args.action;
    }
  }
  const response = await api.get('/lists/zones', { params });
  return response.data;
});

// GET /zones (paginated) — the list page; needs the full page (rows + config).
export const getZonesPaginated = createAsyncThunk<ZonesPage, Record<string, unknown>, { rejectValue: string }>(
  'zones/getZonesPaginated',
  async (params, { rejectWithValue }) => {
    try {
      return await fetchZones(flattenQuery({ limit: initialState.pagination.pageSize, ...params }));
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, 'Failed to fetch zones'));
    }
  }
);

export const deleteZone = createAsyncThunk<void, string, { rejectValue: string }>('zones/deleteZone', async (name, { rejectWithValue }) => {
  try {
    await deleteZoneRequest(name);
  } catch (err) {
    return rejectWithValue(extractErrorMessage(err, 'Failed to delete zone'));
  }
});

const zonesSlice = createSlice({
  name: 'zones',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // getZones
      .addCase(getZones.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.getZones = true;
      })
      .addCase(getZones.fulfilled, (state, action) => {
        state.zones = action.payload;
        state.status = 'succeeded';
        state.loading.getZones = false;
      })
      .addCase(getZones.rejected, (state, action) => {
        state.status = 'failed';
        state.loading.getZones = false;
        state.error = action.payload ?? action.error.message ?? 'Failed to fetch zones';
      })

      // getAuthorizedZones (LOV; unvalidated — see the thunk note)
      .addCase(getAuthorizedZones.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.getAuthorizedZones = true;
      })
      .addCase(getAuthorizedZones.fulfilled, (state, action) => {
        state.zones = action.payload.zones;
        state.status = 'succeeded';
        state.loading.getAuthorizedZones = false;
      })
      .addCase(getAuthorizedZones.rejected, (state, action) => {
        state.status = 'failed';
        state.loading.getAuthorizedZones = false;
        state.error = action.error.message || 'Failed to fetch authorized zones';
      });

    builder
      .addCase(getZonesPaginated.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.getZonesPaginated = true;
      })
      .addCase(getZonesPaginated.fulfilled, (state, action) => {
        state.status = 'succeeded';

        const { zones, config, filtersConfig, pagination } = action.payload;

        state.zones = [...zones];
        state.config = [...config];
        state.filtersConfig = [...filtersConfig];

        state.pagination.totalItems = pagination.total_records;
        state.pagination.totalPages = pagination.total_pages;
        state.pagination.pageSize = pagination.limit;
        state.pagination.currentPage = pagination.current_page > pagination.total_pages ? 1 : pagination.current_page;

        state.loading.getZonesPaginated = false;
      })
      .addCase(getZonesPaginated.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? action.error.message ?? 'Failed to fetch zones';
        state.loading.getZonesPaginated = false;
      });

    builder
      // deleteZone
      .addCase(deleteZone.pending, (state) => {
        state.loading.deleteZone = true;
        state.error = null;
      })
      .addCase(deleteZone.fulfilled, (state, action) => {
        // action.meta.arg is the zone name passed to deleteZone.
        state.zones = state.zones.filter((zone) => zone.name !== action.meta.arg);
        state.status = 'succeeded';
        state.loading.deleteZone = false;
        const toastId = 'data-deleted-zone';
        if (!toast.isActive(toastId)) {
          toast.success(`Zone deleted successfully!`, { toastId });
        }
      })
      .addCase(deleteZone.rejected, (state, action) => {
        state.loading.deleteZone = false;
        state.status = 'failed';
        state.error = action.payload ?? action.error.message ?? 'Failed to delete zone';
        const toastId = 'data-failed-to-delete-zone';
        if (!toast.isActive(toastId)) {
          toast.error(`Failed to delete the zone! ${state.error}`, { toastId });
        }
      });
  },
});

export default zonesSlice.reducer;
